/**
 * Lab Orders Module - Search-Centric Workflow
 * HIPAA-compliant lab order management
 * Features:
 * - Global Patient Search (Name, UHID, Phone)
 * - Patient Verification Mini-Card
 * - Intelligent Visit Resolution
 * - Lab Order CRUD with Visit_ID locking
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search, User, Calendar, Activity, Clock, Plus, FileText,
  AlertTriangle, CheckCircle, XCircle, ChevronRight, Filter,
  RefreshCw, Beaker, Droplets, AlertCircle, Phone, MapPin,
  Heart, Stethoscope, Printer, Edit, Trash2, Eye, Loader2,
  Mail, FileDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== PATIENT SEARCH COMPONENT ====================
const PatientSearch = ({ onPatientSelect, selectedPatient, onClear }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const searchPatients = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/lab-orders/search/patients?q=${encodeURIComponent(searchQuery)}&limit=10`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setResults(data.patients || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (query.trim()) {
        searchPatients(query);
      } else {
        setResults([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchPatients]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (patient) => {
    setQuery('');
    setShowDropdown(false);
    onPatientSelect(patient);
  };

  if (selectedPatient) {
    return null; // Hide search when patient is selected
  }

  return (
    <div ref={searchRef} className="relative">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search patient by Name, Phone, or UHID..."
          className="pl-12 pr-12 h-14 text-lg bg-white border-2 border-gray-200 focus:border-blue-500 rounded-xl"
          data-testid="patient-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 animate-spin" />
        )}
      </div>

      <AnimatePresence>
        {showDropdown && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          >
            <div className="p-2 bg-gray-50 border-b text-xs text-gray-500 font-medium">
              {results.length} patient(s) found
            </div>
            <div className="max-h-80 overflow-y-auto">
              {results.map((patient) => (
                <button
                  key={patient.id}
                  onClick={() => handleSelect(patient)}
                  className="w-full p-3 flex items-center gap-4 hover:bg-blue-50 transition-colors text-left border-b last:border-b-0"
                  data-testid={`patient-result-${patient.patient_number}`}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                    {patient.full_name?.charAt(0) || 'P'}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{patient.full_name}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-3">
                      <span className="font-mono">{patient.patient_number}</span>
                      {patient.date_of_birth && (
                        <span>DOB: {new Date(patient.date_of_birth).toLocaleDateString()}</span>
                      )}
                      {patient.gender && (
                        <span className="capitalize">{patient.gender}</span>
                      )}
                    </div>
                    {patient.phone && (
                      <div className="text-sm text-gray-400">{patient.phone}</div>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {showDropdown && query.length >= 2 && results.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-lg border p-6 text-center"
          >
            <User className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">No patients found matching &quot;{query}&quot;</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== PATIENT VERIFICATION CARD ====================
const PatientVerificationCard = ({ patient, visits, recommendedVisit, onVisitSelect, selectedVisit, onClearPatient }) => {
  const [showVisitDropdown, setShowVisitDropdown] = useState(false);

  const formatDOB = (dob) => {
    if (!dob) return 'N/A';
    return new Date(dob).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200"
    >
      {/* Patient Info - Non-editable verification block */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
            {patient.full_name?.charAt(0) || 'P'}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{patient.full_name}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Badge variant="outline" className="font-mono">{patient.patient_number}</Badge>
              {patient.blood_group && (
                <Badge className="bg-red-100 text-red-700">{patient.blood_group}</Badge>
              )}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearPatient}
          className="text-gray-400 hover:text-red-500"
        >
          <XCircle className="w-5 h-5" />
        </Button>
      </div>

      {/* Verification Details */}
      <div className="grid grid-cols-4 gap-4 p-4 bg-white/60 rounded-xl mb-4">
        <div>
          <span className="text-xs text-gray-500 uppercase tracking-wide">Date of Birth</span>
          <p className="font-semibold text-gray-900">{formatDOB(patient.date_of_birth)}</p>
        </div>
        <div>
          <span className="text-xs text-gray-500 uppercase tracking-wide">Age</span>
          <p className="font-semibold text-gray-900">{patient.age ? `${patient.age} years` : 'N/A'}</p>
        </div>
        <div>
          <span className="text-xs text-gray-500 uppercase tracking-wide">Gender</span>
          <p className="font-semibold text-gray-900 capitalize">{patient.gender || 'N/A'}</p>
        </div>
        <div>
          <span className="text-xs text-gray-500 uppercase tracking-wide">Phone</span>
          <p className="font-semibold text-gray-900">{patient.phone || 'N/A'}</p>
        </div>
      </div>

      {/* Visit Resolution */}
      <div className="border-t border-blue-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Active Visit</span>
          {visits.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowVisitDropdown(!showVisitDropdown)}
              className="text-blue-600"
            >
              Change Visit
            </Button>
          )}
        </div>

        {visits.length === 0 ? (
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">No active visit - Standalone Lab Order</p>
              <p className="text-sm text-blue-600">Lab order will be created without linking to a visit.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Selected/Recommended Visit */}
            <div 
              className="p-4 bg-white rounded-xl border-2 border-green-200 flex items-center gap-4 cursor-pointer hover:border-green-400 transition-colors"
              onClick={() => visits.length > 1 && setShowVisitDropdown(!showVisitDropdown)}
            >
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">
                    {selectedVisit?.visit_number || recommendedVisit?.visit_number}
                  </span>
                  <Badge className="bg-green-100 text-green-700">
                    {selectedVisit?.status || recommendedVisit?.status}
                  </Badge>
                  {(selectedVisit || recommendedVisit)?.visit_type && (
                    <Badge variant="outline" className="capitalize">
                      {(selectedVisit || recommendedVisit)?.visit_type}
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {(selectedVisit || recommendedVisit)?.doctor_name && (
                    <span>Dr. {(selectedVisit || recommendedVisit)?.doctor_name} • </span>
                  )}
                  {(selectedVisit || recommendedVisit)?.chief_complaint || 'General Consultation'}
                </div>
              </div>
              {visits.length > 1 && (
                <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showVisitDropdown ? 'rotate-90' : ''}`} />
              )}
            </div>

            {/* Visit Dropdown */}
            <AnimatePresence>
              {showVisitDropdown && visits.length > 1 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 space-y-2"
                >
                  {visits.map((visit) => (
                    <button
                      key={visit.visit_id}
                      onClick={() => {
                        onVisitSelect(visit);
                        setShowVisitDropdown(false);
                      }}
                      className={`w-full p-3 rounded-lg border text-left transition-colors ${
                        (selectedVisit?.visit_id || recommendedVisit?.visit_id) === visit.visit_id
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{visit.visit_number}</span>
                        <Badge variant="outline">{visit.status}</Badge>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {visit.chief_complaint || visit.visit_type}
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </motion.div>
  );
};

// ==================== LAB ORDER FORM ====================
const LabOrderForm = ({ patient, visit, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [commonTests, setCommonTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [formData, setFormData] = useState({
    priority: 'routine',
    clinical_notes: '',
    fasting_required: false,
    special_instructions: ''
  });
  const [customTest, setCustomTest] = useState({ name: '', sample_type: 'blood' });

  useEffect(() => {
    // Fetch common tests
    const fetchTests = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/lab-orders/common-tests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setCommonTests(data.tests || []);
      } catch (error) {
        console.error('Failed to fetch common tests:', error);
      }
    };
    fetchTests();
  }, []);

  const toggleTest = (test) => {
    setSelectedTests(prev => {
      const exists = prev.find(t => t.test_code === test.code);
      if (exists) {
        return prev.filter(t => t.test_code !== test.code);
      }
      return [...prev, {
        test_name: test.name,
        test_code: test.code,
        sample_type: test.sample_type
      }];
    });
  };

  const addCustomTest = () => {
    if (!customTest.name.trim()) {
      toast.error('Please enter a test name');
      return;
    }
    setSelectedTests(prev => [...prev, {
      test_name: customTest.name,
      test_code: null,
      sample_type: customTest.sample_type
    }]);
    setCustomTest({ name: '', sample_type: 'blood' });
  };

  const removeTest = (index) => {
    setSelectedTests(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (selectedTests.length === 0) {
      toast.error('Please select at least one test');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/lab-orders/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: patient.id,
          visit_id: visit?.visit_id || null,  // Visit is now optional
          tests: selectedTests,
          ...formData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create lab order');
      }

      toast.success(`Lab Order ${data.order_number} created successfully!`);
      onSuccess(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Selected Tests */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Beaker className="w-5 h-5 text-blue-600" />
            Selected Tests ({selectedTests.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedTests.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No tests selected. Choose from common tests below or add custom.</p>
          ) : (
            <div className="space-y-2">
              {selectedTests.map((test, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Droplets className="w-4 h-4 text-blue-600" />
                    <div>
                      <span className="font-medium">{test.test_name}</span>
                      {test.test_code && (
                        <Badge variant="outline" className="ml-2 text-xs">{test.test_code}</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="capitalize">{test.sample_type}</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTest(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Common Tests */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Common Lab Tests</CardTitle>
          <CardDescription>Click to add/remove tests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {commonTests.map((test) => {
              const isSelected = selectedTests.some(t => t.test_code === test.code);
              return (
                <button
                  key={test.code}
                  onClick={() => toggleTest(test)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {test.name}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Add Custom Test */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Add Custom Test</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Input
              value={customTest.name}
              onChange={(e) => setCustomTest({...customTest, name: e.target.value})}
              placeholder="Enter test name"
              className="flex-1"
            />
            <Select
              value={customTest.sample_type}
              onValueChange={(v) => setCustomTest({...customTest, sample_type: v})}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blood">Blood</SelectItem>
                <SelectItem value="urine">Urine</SelectItem>
                <SelectItem value="stool">Stool</SelectItem>
                <SelectItem value="swab">Swab</SelectItem>
                <SelectItem value="tissue">Tissue</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={addCustomTest} variant="outline">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Order Details */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Order Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <Select
                value={formData.priority}
                onValueChange={(v) => setFormData({...formData, priority: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="stat">STAT (Immediate)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Checkbox
                id="fasting"
                checked={formData.fasting_required}
                onCheckedChange={(checked) => setFormData({...formData, fasting_required: checked})}
              />
              <label htmlFor="fasting" className="text-sm font-medium text-gray-700">
                Fasting Required
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Clinical Notes</label>
            <Textarea
              value={formData.clinical_notes}
              onChange={(e) => setFormData({...formData, clinical_notes: e.target.value})}
              placeholder="Enter clinical notes or indication for tests..."
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
            <Textarea
              value={formData.special_instructions}
              onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
              placeholder="Any special instructions for sample collection..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || selectedTests.length === 0}
          className="bg-gradient-to-r from-blue-600 to-indigo-600"
          data-testid="create-lab-order-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              Create Lab Order
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// ==================== LAB ORDERS LIST ====================
const LabOrdersList = ({ onNewOrder, onEnterResults }) => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [emailModal, setEmailModal] = useState({ open: false, order: null });
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [viewModal, setViewModal] = useState({ open: false, order: null });
  const [statusModal, setStatusModal] = useState({ open: false, order: null });
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const searchDebounceRef = useRef(null);

  // Handle status update
  const handleStatusUpdate = async (orderId, newStatus, notes = '') => {
    setUpdatingStatus(orderId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/lab-orders/orders/${orderId}/status?new_status=${newStatus}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update status');
      }
      
      const statusLabels = {
        'sample_collected': 'Sample Collected',
        'in_progress': 'In Progress',
        'completed': 'Completed',
        'cancelled': 'Cancelled'
      };
      
      toast.success(`Order status updated to "${statusLabels[newStatus] || newStatus}"`);
      setStatusModal({ open: false, order: null });
      setViewModal({ open: false, order: null });
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Get next available status options based on current status
  const getStatusOptions = (currentStatus) => {
    const statusFlow = {
      'pending': [
        { value: 'sample_collected', label: 'Mark Sample Collected', icon: '🧪', color: 'bg-yellow-500' },
        { value: 'cancelled', label: 'Cancel Order', icon: '❌', color: 'bg-red-500' }
      ],
      'sample_collected': [
        { value: 'in_progress', label: 'Start Processing', icon: '⚙️', color: 'bg-blue-500' },
        { value: 'cancelled', label: 'Cancel Order', icon: '❌', color: 'bg-red-500' }
      ],
      'in_progress': [
        // Results entry handles completion
        { value: 'cancelled', label: 'Cancel Order', icon: '❌', color: 'bg-red-500' }
      ],
      'completed': [],
      'cancelled': []
    };
    return statusFlow[currentStatus] || [];
  };

  const handleDownloadPdf = async (order) => {
    if (order.status !== 'completed') {
      toast.error('PDF report is only available for completed orders');
      return;
    }
    
    setDownloadingPdf(order.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/lab-orders/orders/${order.id}/report/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Lab_Report_${order.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleEmailReport = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/lab-orders/orders/${emailModal.order.id}/report/email?email_to=${encodeURIComponent(emailAddress)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }
      
      toast.success(`Lab report emailed to ${emailAddress}`);
      setEmailModal({ open: false, order: null });
      setEmailAddress('');
    } catch (error) {
      toast.error(error.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const clearFilters = () => {
    setFilter('all');
    setPriorityFilter('all');
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setCriticalOnly(false);
  };

  const hasActiveFilters = filter !== 'all' || priorityFilter !== 'all' || searchQuery || dateFrom || dateTo || criticalOnly;

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Build query parameters
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (criticalOnly) params.append('critical_only', 'true');

      const queryString = params.toString();
      const ordersUrl = `${API_URL}/api/lab-orders/orders${queryString ? `?${queryString}` : ''}`;

      const [ordersRes, statsRes] = await Promise.all([
        fetch(ordersUrl, { headers }),
        fetch(`${API_URL}/api/lab-orders/stats`, { headers })
      ]);

      const ordersData = await ordersRes.json();
      const statsData = await statsRes.json();

      setOrders(ordersData.orders || []);
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to load lab orders');
    } finally {
      setLoading(false);
    }
  }, [filter, priorityFilter, searchQuery, dateFrom, dateTo, criticalOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounced search effect
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchData();
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, fetchData]);

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      sample_collected: { color: 'bg-blue-100 text-blue-800', icon: Droplets },
      in_progress: { color: 'bg-purple-100 text-purple-800', icon: Activity },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={c.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      routine: 'bg-gray-100 text-gray-700',
      urgent: 'bg-orange-100 text-orange-700',
      stat: 'bg-red-100 text-red-700'
    };
    return (
      <Badge className={config[priority] || config.routine}>
        {priority === 'stat' ? 'STAT' : priority}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-yellow-50 to-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">Pending</p>
                  <p className="text-2xl font-bold text-yellow-800">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500 opacity-60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">Sample Collected</p>
                  <p className="text-2xl font-bold text-blue-800">{stats.sample_collected}</p>
                </div>
                <Droplets className="w-8 h-8 text-blue-500 opacity-60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700">In Progress</p>
                  <p className="text-2xl font-bold text-purple-800">{stats.in_progress}</p>
                </div>
                <Activity className="w-8 h-8 text-purple-500 opacity-60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Completed Today</p>
                  <p className="text-2xl font-bold text-green-800">{stats.completed_today}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500 opacity-60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">STAT/Urgent</p>
                  <p className="text-2xl font-bold text-red-800">{stats.stat_orders + stats.urgent_orders}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500 opacity-60" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Enhanced Filters & Search */}
      <Card>
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by order number, patient name, or UHID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="orders-search-input"
              />
            </div>

            {/* Filter Row */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Status Filter */}
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sample_collected">Sample Collected</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              {/* Priority Filter */}
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="stat">STAT</SelectItem>
                </SelectContent>
              </Select>

              {/* Date Range */}
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  placeholder="From date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-40"
                />
                <span className="text-gray-400">to</span>
                <Input
                  type="date"
                  placeholder="To date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-40"
                />
              </div>

              {/* Critical Only Toggle */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="critical-only"
                  checked={criticalOnly}
                  onCheckedChange={setCriticalOnly}
                />
                <label htmlFor="critical-only" className="text-sm font-medium text-gray-700">
                  Critical values only
                </label>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <XCircle className="w-4 h-4 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {orders.length} order(s) found
          {hasActiveFilters && <span className="ml-2 text-blue-600">• Filters applied</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={onNewOrder} className="bg-gradient-to-r from-blue-600 to-indigo-600">
            <Plus className="w-4 h-4 mr-2" />
            New Lab Order
          </Button>
        </div>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {orders.length === 0 ? (
            <div className="p-12 text-center">
              <Beaker className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No lab orders found</h3>
              <p className="text-gray-500">Create a new lab order to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tests</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordered</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono font-medium">{order.order_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-gray-900">{order.patient_name}</div>
                          <div className="text-sm text-gray-500">{order.patient_number}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm">{order.tests?.length || 0} test(s)</span>
                      </td>
                      <td className="px-4 py-3">
                        {getPriorityBadge(order.priority)}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-500">
                          {new Date(order.ordered_at).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            title="View Details"
                            onClick={() => setViewModal({ open: true, order })}
                            data-testid={`view-order-${order.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          {/* Status Update Button - for pending orders */}
                          {order.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusUpdate(order.id, 'sample_collected')}
                              disabled={updatingStatus === order.id}
                              className="text-yellow-600 hover:text-yellow-700"
                              title="Mark Sample Collected"
                              data-testid={`collect-sample-${order.id}`}
                            >
                              {updatingStatus === order.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Beaker className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          
                          {/* Status Update Button - for sample_collected orders */}
                          {order.status === 'sample_collected' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusUpdate(order.id, 'in_progress')}
                              disabled={updatingStatus === order.id}
                              className="text-blue-600 hover:text-blue-700"
                              title="Start Processing"
                              data-testid={`start-processing-${order.id}`}
                            >
                              {updatingStatus === order.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Activity className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          
                          {/* Enter Results - for sample_collected or in_progress */}
                          {(order.status === 'sample_collected' || order.status === 'in_progress') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEnterResults(order)}
                              className="text-green-600 hover:text-green-700"
                              title="Enter Results"
                              data-testid={`enter-results-${order.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Critical badge */}
                          {order.status === 'completed' && order.has_critical_values && (
                            <Badge className="bg-red-100 text-red-700 animate-pulse">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Critical
                            </Badge>
                          )}
                          
                          {/* PDF/Email for completed orders */}
                          {order.status === 'completed' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadPdf(order)}
                                disabled={downloadingPdf === order.id}
                                title="Download PDF Report"
                                className="text-blue-600 hover:text-blue-700"
                                data-testid={`download-pdf-${order.id}`}
                              >
                                {downloadingPdf === order.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <FileDown className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEmailModal({ open: true, order })}
                                title="Email Report"
                                className="text-purple-600 hover:text-purple-700"
                                data-testid={`email-report-${order.id}`}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" title="Print">
                            <Printer className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Report Modal */}
      {emailModal.open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-md"
          >
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">Email Lab Report</h3>
              <p className="text-sm text-gray-500">
                Send {emailModal.order?.order_number} report via email
              </p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                placeholder="patient@example.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                data-testid="email-input"
              />
              <p className="text-xs text-gray-500 mt-2">
                The PDF report will be sent as an attachment
              </p>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEmailModal({ open: false, order: null });
                  setEmailAddress('');
                }}
                disabled={sendingEmail}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEmailReport}
                disabled={sendingEmail || !emailAddress}
                className="bg-gradient-to-r from-purple-600 to-indigo-600"
                data-testid="send-email-btn"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* View Order Details Modal */}
      {viewModal.open && viewModal.order && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setViewModal({ open: false, order: null })}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Lab Order Details</h2>
                  <p className="text-blue-100">{viewModal.order.order_number}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setViewModal({ open: false, order: null })}
                  className="text-white hover:bg-white/20"
                >
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Patient Info */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Patient Information</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Patient Name</p>
                      <p className="font-medium">{viewModal.order.patient_name || 'Unknown'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Patient ID (UHID)</p>
                      <p className="font-medium">{viewModal.order.patient_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Visit ID</p>
                      <p className="font-mono text-sm">{viewModal.order.visit_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Order Date</p>
                      <p className="font-medium">
                        {viewModal.order.ordered_at 
                          ? new Date(viewModal.order.ordered_at).toLocaleString() 
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Info */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Order Information</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      {getStatusBadge(viewModal.order.status)}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Priority</p>
                      {getPriorityBadge(viewModal.order.priority)}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Ordered By</p>
                      <p className="font-medium">{viewModal.order.ordered_by || '-'}</p>
                    </div>
                  </div>
                  {viewModal.order.clinical_notes && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs text-gray-500">Clinical Notes</p>
                      <p className="text-sm mt-1">{viewModal.order.clinical_notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tests */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                  Ordered Tests ({viewModal.order.tests?.length || 0})
                </h3>
                <div className="space-y-2">
                  {viewModal.order.tests?.map((test, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Beaker className="w-4 h-4 text-blue-600" />
                        <div>
                          <p className="font-medium">{test.test_name}</p>
                          <p className="text-xs text-gray-500">
                            {test.test_code} • {test.sample_type}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline">{test.sample_type}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Results (if completed) */}
              {viewModal.order.status === 'completed' && viewModal.order.results && viewModal.order.results.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Test Results
                    {viewModal.order.has_critical_values && (
                      <Badge className="ml-2 bg-red-100 text-red-700">Critical Values</Badge>
                    )}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 font-medium">Test</th>
                          <th className="pb-2 font-medium">Result</th>
                          <th className="pb-2 font-medium">Reference</th>
                          <th className="pb-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewModal.order.results.map((result, index) => (
                          <tr key={index} className={`border-b ${result.is_critical ? 'bg-red-50' : ''}`}>
                            <td className="py-2 font-medium">{result.test_name}</td>
                            <td className="py-2">
                              {result.result_value} {result.unit}
                            </td>
                            <td className="py-2 text-gray-500">{result.reference_range_text || '-'}</td>
                            <td className="py-2">
                              <Badge className={
                                result.flag === 'normal' ? 'bg-green-100 text-green-700' :
                                result.flag === 'low' ? 'bg-blue-100 text-blue-700' :
                                result.flag === 'high' ? 'bg-orange-100 text-orange-700' :
                                result.flag?.includes('critical') ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {result.flag === 'critical_low' ? 'Critical Low' :
                                 result.flag === 'critical_high' ? 'Critical High' :
                                 result.flag?.charAt(0).toUpperCase() + result.flag?.slice(1)}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {viewModal.order.results_summary && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-600 font-medium">Lab Notes</p>
                      <p className="text-sm mt-1">{viewModal.order.results_summary}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Timeline */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Timeline</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium">Order Created</p>
                      <p className="text-xs text-gray-500">
                        {viewModal.order.created_at 
                          ? new Date(viewModal.order.created_at).toLocaleString() 
                          : '-'}
                      </p>
                    </div>
                  </div>
                  {viewModal.order.sample_collected_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium">Sample Collected</p>
                        <p className="text-xs text-gray-500">
                          {new Date(viewModal.order.sample_collected_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                  {viewModal.order.completed_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium">Results Completed</p>
                        <p className="text-xs text-gray-500">
                          {new Date(viewModal.order.completed_at).toLocaleString()}
                          {viewModal.order.completed_by && ` by ${viewModal.order.completed_by}`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Order ID: {viewModal.order.id?.slice(0, 8)}...
              </div>
              <div className="flex items-center gap-2">
                {/* Status Update Actions */}
                {viewModal.order.status === 'pending' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusUpdate(viewModal.order.id, 'sample_collected')}
                    disabled={updatingStatus === viewModal.order.id}
                    className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                    data-testid="modal-collect-sample-btn"
                  >
                    {updatingStatus === viewModal.order.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Beaker className="w-4 h-4 mr-2" />
                    )}
                    Mark Sample Collected
                  </Button>
                )}
                
                {viewModal.order.status === 'sample_collected' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate(viewModal.order.id, 'in_progress')}
                      disabled={updatingStatus === viewModal.order.id}
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                      data-testid="modal-start-processing-btn"
                    >
                      {updatingStatus === viewModal.order.id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Activity className="w-4 h-4 mr-2" />
                      )}
                      Start Processing
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setViewModal({ open: false, order: null });
                        onEnterResults(viewModal.order);
                      }}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="modal-enter-results-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Enter Results
                    </Button>
                  </>
                )}
                
                {viewModal.order.status === 'in_progress' && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setViewModal({ open: false, order: null });
                      onEnterResults(viewModal.order);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="modal-enter-results-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Enter Results
                  </Button>
                )}
                
                {/* Cancel option for non-completed orders */}
                {!['completed', 'cancelled'].includes(viewModal.order.status) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (confirm('Are you sure you want to cancel this order?')) {
                        handleStatusUpdate(viewModal.order.id, 'cancelled');
                      }
                    }}
                    disabled={updatingStatus === viewModal.order.id}
                    className="border-red-500 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Order
                  </Button>
                )}
                
                {viewModal.order.status === 'completed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleDownloadPdf(viewModal.order);
                    }}
                    disabled={downloadingPdf === viewModal.order.id}
                  >
                    {downloadingPdf === viewModal.order.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileDown className="w-4 h-4 mr-2" />
                    )}
                    Download PDF
                  </Button>
                )}
                <Button
                  onClick={() => setViewModal({ open: false, order: null })}
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

    </div>
  );
};

// ==================== LAB RESULT ENTRY COMPONENT ====================
const LabResultEntry = ({ order, onClose, onResultsSubmitted }) => {
  const [results, setResults] = useState([]);
  const [labNotes, setLabNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [referenceRanges, setReferenceRanges] = useState({});

  useEffect(() => {
    // Initialize results from order tests
    if (order?.tests) {
      const initialResults = order.tests.map(test => ({
        test_name: test.test_name,
        test_code: test.test_code,
        result_value: '',
        unit: '',
        reference_range_text: '',
        flag: 'normal',
        notes: ''
      }));
      setResults(initialResults);
      
      // Fetch reference ranges for each test
      order.tests.forEach(test => {
        if (test.test_code) {
          fetchReferenceRanges(test.test_code);
        }
      });
    }
  }, [order]);

  const fetchReferenceRanges = async (testCode) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/lab-orders/reference-ranges/${testCode}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setReferenceRanges(prev => ({ ...prev, [testCode]: data.ranges }));
      }
    } catch (error) {
      console.error('Failed to fetch reference ranges:', error);
    }
  };

  const updateResult = (index, field, value) => {
    setResults(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Auto-flag based on reference range
      if (field === 'result_value' && updated[index].test_code) {
        const ranges = referenceRanges[updated[index].test_code];
        if (ranges) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            // Check for first parameter in ranges (simplified)
            const firstParam = Object.keys(ranges)[0];
            if (firstParam && ranges[firstParam]) {
              const range = ranges[firstParam];
              const low = range.low || range.male?.low || range.female?.low;
              const high = range.high || range.male?.high || range.female?.high;
              const critLow = range.critical_low;
              const critHigh = range.critical_high;
              
              if (critLow && numValue < critLow) {
                updated[index].flag = 'critical_low';
              } else if (critHigh && numValue > critHigh) {
                updated[index].flag = 'critical_high';
              } else if (low && numValue < low) {
                updated[index].flag = 'low';
              } else if (high && numValue > high) {
                updated[index].flag = 'high';
              } else {
                updated[index].flag = 'normal';
              }
            }
          }
        }
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    // Validate all results have values
    const emptyResults = results.filter(r => !r.result_value.trim());
    if (emptyResults.length > 0) {
      toast.error(`Please enter results for all ${emptyResults.length} tests`);
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/lab-orders/orders/${order.id}/results`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            results: results,
            lab_notes: labNotes
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit results');
      }

      const data = await response.json();
      
      if (data.has_critical_values) {
        toast.warning(`Results submitted with ${data.critical_count} CRITICAL value(s)! Doctor has been notified.`, {
          duration: 5000
        });
      } else {
        toast.success('Lab results submitted successfully');
      }
      
      onResultsSubmitted?.();
      onClose();
    } catch (error) {
      console.error('Failed to submit results:', error);
      toast.error(error.message || 'Failed to submit results');
    } finally {
      setSubmitting(false);
    }
  };

  const getFlagBadge = (flag) => {
    const config = {
      normal: { className: 'bg-green-100 text-green-700', label: 'Normal' },
      low: { className: 'bg-blue-100 text-blue-700', label: 'Low' },
      high: { className: 'bg-orange-100 text-orange-700', label: 'High' },
      critical_low: { className: 'bg-red-100 text-red-700 animate-pulse', label: '⚠️ Critical Low' },
      critical_high: { className: 'bg-red-100 text-red-700 animate-pulse', label: '⚠️ Critical High' }
    };
    const { className, label } = config[flag] || config.normal;
    return <Badge className={className}>{label}</Badge>;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Enter Lab Results</h2>
              <p className="text-blue-100">
                {order?.order_number} • {order?.patient_name}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <XCircle className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {results.map((result, index) => (
              <Card key={index} className={result.flag?.includes('critical') ? 'border-red-300 bg-red-50' : ''}>
                <CardContent className="p-4">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-3">
                      <label className="text-sm font-medium text-gray-700">{result.test_name}</label>
                      {result.test_code && (
                        <Badge variant="outline" className="ml-2 text-xs">{result.test_code}</Badge>
                      )}
                    </div>
                    
                    <div className="col-span-3">
                      <Input
                        placeholder="Result value"
                        value={result.result_value}
                        onChange={(e) => updateResult(index, 'result_value', e.target.value)}
                        className={result.flag?.includes('critical') ? 'border-red-400' : ''}
                        data-testid={`result-value-${index}`}
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Input
                        placeholder="Unit"
                        value={result.unit}
                        onChange={(e) => updateResult(index, 'unit', e.target.value)}
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Input
                        placeholder="Reference range"
                        value={result.reference_range_text}
                        onChange={(e) => updateResult(index, 'reference_range_text', e.target.value)}
                      />
                    </div>
                    
                    <div className="col-span-2 text-right">
                      {getFlagBadge(result.flag)}
                    </div>
                  </div>
                  
                  {/* Flag selector for manual override */}
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Flag:</span>
                    <Select value={result.flag} onValueChange={(v) => updateResult(index, 'flag', v)}>
                      <SelectTrigger className="w-32 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical_low">Critical Low</SelectItem>
                        <SelectItem value="critical_high">Critical High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Lab Notes */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Lab Notes / Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Add any additional notes or observations..."
                  value={labNotes}
                  onChange={(e) => setLabNotes(e.target.value)}
                  rows={3}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {results.filter(r => r.flag?.includes('critical')).length > 0 && (
              <span className="text-red-600 font-medium flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {results.filter(r => r.flag?.includes('critical')).length} critical value(s) detected
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-gradient-to-r from-green-600 to-emerald-600"
              data-testid="submit-results-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit Results
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================
const LabOrders = () => {
  const [mode, setMode] = useState('list'); // 'list' or 'create' or 'results'
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientVisits, setPatientVisits] = useState([]);
  const [recommendedVisit, setRecommendedVisit] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchPatientVisits = async (patient) => {
    setLoadingVisits(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/lab-orders/search/patients/${patient.id}/visits`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to load patient visits');

      const data = await response.json();
      setPatientVisits(data.active_visits || []);
      setRecommendedVisit(data.recommended_visit);
      setSelectedVisit(data.recommended_visit);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingVisits(false);
    }
  };

  const handlePatientSelect = async (patient) => {
    setSelectedPatient(patient);
    await fetchPatientVisits(patient);
  };

  const handleClearPatient = () => {
    setSelectedPatient(null);
    setPatientVisits([]);
    setRecommendedVisit(null);
    setSelectedVisit(null);
  };

  const handleOrderSuccess = () => {
    handleClearPatient();
    setMode('list');
  };

  return (
    <div className="space-y-6" data-testid="lab-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lab Orders</h1>
          <p className="text-gray-500">
            {mode === 'create' ? 'Create a new lab order' : 'Manage laboratory orders'}
          </p>
        </div>
        {mode === 'list' && (
          <Button
            onClick={() => setMode('create')}
            className="bg-gradient-to-r from-blue-600 to-indigo-600"
            data-testid="new-lab-order-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Lab Order
            </Button>
          )}
        </div>

        {mode === 'list' ? (
          <LabOrdersList 
            onNewOrder={() => setMode('create')} 
            onEnterResults={(order) => {
              setSelectedOrder(order);
              setMode('results');
            }}
          />
        ) : mode === 'results' ? (
          <LabResultEntry
            order={selectedOrder}
            onClose={() => {
              setSelectedOrder(null);
              setMode('list');
            }}
            onResultsSubmitted={() => {
              setSelectedOrder(null);
              setMode('list');
            }}
          />
        ) : (
          <div className="space-y-6">
            {/* Back Button */}
            <Button variant="ghost" onClick={() => { setMode('list'); handleClearPatient(); }}>
              ← Back to Lab Orders
            </Button>

            {/* Patient Search */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-600" />
                  Step 1: Find Patient
                </CardTitle>
                <CardDescription>
                  Search by Patient Name, Phone Number, or UHID
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PatientSearch
                  onPatientSelect={handlePatientSelect}
                  selectedPatient={selectedPatient}
                  onClear={handleClearPatient}
                />

                {loadingVisits && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    <span className="ml-2 text-gray-500">Loading patient details...</span>
                  </div>
                )}

                {selectedPatient && !loadingVisits && (
                  <div className="mt-4">
                    <PatientVerificationCard
                      patient={selectedPatient}
                      visits={patientVisits}
                      recommendedVisit={recommendedVisit}
                      selectedVisit={selectedVisit}
                      onVisitSelect={setSelectedVisit}
                      onClearPatient={handleClearPatient}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lab Order Form - Show if patient is selected (visit is now optional) */}
            {selectedPatient && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Beaker className="w-5 h-5 text-blue-600" />
                    Step 2: Select Tests
                  </CardTitle>
                  <CardDescription>
                    Choose laboratory tests for this order
                    {!selectedVisit && <span className="text-blue-600 ml-2">(No visit linked - standalone order)</span>}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <LabOrderForm
                    patient={selectedPatient}
                    visit={selectedVisit}
                    onSuccess={handleOrderSuccess}
                    onCancel={() => { setMode('list'); handleClearPatient(); }}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
  );
};

export default LabOrders;
